import React from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import {
    toggleModal,
    togglePopup,
    toggleSpinner,
    updateAuthPromoBlock,
    updateCurrentSiteSection,
    updateMessages,
    updateReduxAction,
    toggleSnackBar,
} from 'apps/auth/actions/appAction';
import { updateAuth } from 'apps/auth/actions/authAction';
import { initCalculator } from 'apps/auth/actions/calculatorAction';
import { updateCards } from 'apps/auth/actions/cardsAction';
import { chatBlockStatus, toggleChat } from 'apps/auth/actions/chatAction';
import { setExtra } from 'apps/auth/actions/extraAction';
import { setNewForm } from 'apps/auth/actions/formAction';
import { setCheque, setHistory } from 'apps/auth/actions/historyAction';
import { updateJsMessages, updatePageMessages } from 'apps/auth/actions/messageAction';
import { initTagManagerOnce } from 'apps/auth/analytics/analytics';
import { brazeChangeUser, brazePgaViewEvent } from 'apps/auth/analytics/braze';
import BrazeIamSlideupHost from 'apps/auth/components/braze/BrazeIamSlideupHost';
import WSChat from 'apps/auth/components/chat/WSChat';
import { AMPLITUDE_API_KEY, setAmplitudeNoopInstance } from 'apps/auth/components/utils/AmplitudeUtils';
import { COUNTRY_OF_RESIDENCE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import update from 'apps/auth/helpers/Immutability';
import { ConsentService } from 'apps/auth/services/ConsentService';
import { UpdateReduxService } from 'apps/auth/services/UpdateReduxService';

const PageWrapper = styled.div`
    background-color: ${(props) => props.theme.white};
    color: ${(props) => props.theme.black};
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export default function BasePage(PageComponent) {
    class BasePageComponent extends React.Component {
        constructor(props) {
            super(props);

            this.updateReduxService = new UpdateReduxService(props.lang);
            this.updateReduxService.setMessageAction(props.updateJsMessages, props.updatePageMessages);
            this.updateReduxService.setHistoryAction(props.setCheque, props.setHistory);
            this.updateReduxService.setChatAction(props.chatBlockStatus);
            this.updateReduxService.setAuthAction(props.updateAuth);
            this.updateReduxService.setFormAction(props.setNewForm);
            this.updateReduxService.setCalculatorAction(props.initCalculator);
            this.updateReduxService.setAppAction(
                props.toggleModal,
                props.togglePopup,
                props.toggleSpinner,
                props.updateAuthPromoBlock,
                props.updateCurrentSiteSection,
                props.updateMessages,
            );
            this.updateReduxService.setExtraAction(props.setExtra);
            this.updateReduxService.setCardsAction(props.updateCards);
            this.updateReduxService.setStateOptions(props.stateOptions);

            const data = __isBrowser__ ? window.__INITIAL_DATA__ : props.staticContext ? props.staticContext.json : props.data;
            this.state = { data: data, loading: props.updateRedux };

            this.amplitudeInitialized = false;
        }

        componentDidMount() {
            if (this.props.updateRedux) {
                this.updateReduxService.fetchData(this.props.lang, this.props.isAuthZone, this.props.country).then((data) => {
                    this.setState(
                        update(this.state, {
                            data: { $set: data },
                            loading: { $set: false },
                        }),
                    );
                });
            } else {
                this.props.updateReduxAction();
            }
            window.scrollTo(0, 0);
            if (__isBrowser__ && __isProd__) {
                initTagManagerOnce();
            }
            if (!__isProd__) {
                setAmplitudeNoopInstance();
            }
            if (__isBrowser__) {
                const tryInitAnalytics = () => {
                    if (!ConsentService.hasLoaded()) return;
                    const { analytics } = ConsentService.getConsentState();
                    if (analytics) {
                        if (!this.amplitudeInitialized && __isProd__) {
                            const userId = this.props.auth?.phone ? this.props.auth?.phone : null;
                            const userCountry = this.props.userCountry?.nameEn;
                            amplitude.getInstance().init(AMPLITUDE_API_KEY, userId, {
                                serverZone: 'EU',
                                serverZoneBasedApi: true,
                            });
                            if (this.props.webAppVersion) {
                                amplitude.getInstance().setVersionName(this.props.webAppVersion);
                            }
                            if (userCountry) {
                                amplitude.getInstance().setUserProperties({
                                    [COUNTRY_OF_RESIDENCE]: userCountry,
                                });
                            }
                            this.amplitudeInitialized = true;
                        }
                    } else {
                        // User re-consent (rejection)
                        if (this.amplitudeInitialized) {
                            try {
                                amplitude.getInstance().setOptOut(true);
                            } catch (e) {
                                if (__isDev__) {
                                    console.warn('[Consent] Failed to disable Amplitude', e);
                                }
                            }
                            this.amplitudeInitialized = false;
                        }
                        setAmplitudeNoopInstance();
                    }
                };

                tryInitAnalytics();
            }
            if (__isBrowser__) {
                if (this.props.auth?.ballId) {
                    brazePgaViewEvent(window.location.href, document.title, this.props.app, this.props.auth, this.props.lang);
                    brazeChangeUser(this.props.auth?.ballId);
                } else {
                    sessionStorage?.removeItem('isLoggedIn');
                }
            }
        }

        componentDidUpdate(prevProps) {
            if (this.props.lang !== prevProps.lang) {
                this.updateReduxService.fetchData(this.props.lang, this.props.isAuthZone, this.props.country);
            }
        }

        render() {
            return (
                <PageWrapper>
                    <PageComponent {...this.props} loading={this.state.loading} data={this.state.data} />
                    {this.props.stateOptions?.showChat && <WSChat />}
                    <BrazeIamSlideupHost />
                </PageWrapper>
            );
        }
    }

    const mapStateToProps = (state) => {
        return {
            app: state.app,
            updateRedux: state.app.updateRedux,
            auth: state.auth,
            lang: state.language.lang,
            country: state.app.country,
            showBrexitTerms: state.extra.showBrexitTerms,
            userCountry: state.extra?.userCountry,
            webAppVersion: state.extra?.webAppVersion,
        };
    };

    return connect(mapStateToProps, {
        updateReduxAction,
        togglePopup,
        toggleModal,
        updatePageMessages,
        updateJsMessages,
        updateAuthPromoBlock,
        setCheque,
        setHistory,
        setNewForm,
        initCalculator,
        updateMessages,
        toggleSpinner,
        updateAuth,
        updateCards,
        setExtra,
        updateCurrentSiteSection,
        chatBlockStatus,
        toggleChat,
        toggleSnackBar,
    })(BasePageComponent);
}
